import React, { useState, useRef, useEffect } from 'react';
import { Move, BrainCircuit, ZoomIn, ZoomOut } from 'lucide-react';



const MindMapView = ({ data }) => {
    const containerRef = useRef(null);

    // State
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [viewport, setViewport] = useState({ x: 0, y: 0, scale: 1 });

    // Interaction State
    const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
    const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
    const [draggedNodeId, setDraggedNodeId] = useState(null);

    // Simulation Refs (mutable for performance)
    const simulationRef = useRef({
        nodes: [],
        edges: [],
        running: false,
        animationId: null
    });

    // Initialize Data
    useEffect(() => {
        let safeData = data;
        if (typeof data === 'string') {
            try { safeData = JSON.parse(data); } catch (e) { safeData = null; }
        }

        if (safeData && Array.isArray(safeData.nodes)) {
            // Normalize edges
            const normEdges = (Array.isArray(safeData.edges) ? safeData.edges : []).map(e => ({
                id: e.id || `e-${e.source || e.from}-${e.target || e.to}`,
                source: e.source || e.from,
                target: e.target || e.to,
                label: e.label || ''
            }));

            // Initialize nodes with positions if missing
            const normNodes = safeData.nodes.map(n => ({
                ...n,
                x: n.x ?? (Math.random() * 100 - 50), // Random jitter if 0
                y: n.y ?? (Math.random() * 100 - 50),
                vx: 0,
                vy: 0
            }));

            setNodes(normNodes);
            setEdges(normEdges);

            // Update simulation refs
            simulationRef.current.nodes = normNodes;
            simulationRef.current.edges = normEdges;

            // Restart simulation
            if (!simulationRef.current.running) {
                startSimulation();
            }
        }
    }, [data]);

    // Force Directed Simulation Loop
    const startSimulation = () => {
        if (simulationRef.current.running) return;
        simulationRef.current.running = true;

        const update = () => {
            if (!simulationRef.current.running) return;

            const { nodes, edges } = simulationRef.current;


            // Physics Constants
            const REPULSION = 200000; // Much stronger repulsion for spacing
            const SPRING_LENGTH = 350; // Longer springs
            const SPRING_STRENGTH = 0.02; // Weaker springs to allow repulsion to win
            const CENTER_GRAVITY = 0.005; // Weaker gravity to allow expansion
            const DAMPING = 0.7; // More friction for stability

            // 1. Repulsion (Node-Node)
            for (let i = 0; i < nodes.length; i++) {
                const nodeA = nodes[i];
                for (let j = i + 1; j < nodes.length; j++) {
                    const nodeB = nodes[j];
                    const dx = nodeA.x - nodeB.x;
                    const dy = nodeA.y - nodeB.y;
                    const distSq = dx * dx + dy * dy || 1;
                    const dist = Math.sqrt(distSq);

                    const force = REPULSION / distSq;
                    const fx = (dx / dist) * force;
                    const fy = (dy / dist) * force;

                    if (!nodeA.isDragging) { nodeA.vx += fx; nodeA.vy += fy; }
                    if (!nodeB.isDragging) { nodeB.vx -= fx; nodeB.vy -= fy; }
                }
            }

            // 2. Attraction (Edges)
            edges.forEach(edge => {
                const source = nodes.find(n => n.id === edge.source);
                const target = nodes.find(n => n.id === edge.target);
                if (source && target) {
                    const dx = target.x - source.x;
                    const dy = target.y - source.y;
                    const dist = Math.sqrt(dx * dx + dy * dy) || 1;

                    const force = (dist - SPRING_LENGTH) * SPRING_STRENGTH;
                    const fx = (dx / dist) * force;
                    const fy = (dy / dist) * force;

                    if (!source.isDragging) { source.vx += fx; source.vy += fy; }
                    if (!target.isDragging) { target.vx -= fx; target.vy -= fy; }
                }
            });

            // 3. Center Gravity (Keep in view)
            nodes.forEach(node => {
                if (!node.isDragging) {
                    node.vx -= node.x * CENTER_GRAVITY;
                    node.vy -= node.y * CENTER_GRAVITY;

                    // Update Position
                    node.x += node.vx;
                    node.y += node.vy;

                    // Apply Damping
                    node.vx *= DAMPING;
                    node.vy *= DAMPING;
                }
            });

            // Trigger Render
            setNodes([...nodes]);

            // Continue Loop
            simulationRef.current.animationId = requestAnimationFrame(update);
        };

        update();
    };

    // Stop simulation on unmount
    useEffect(() => {
        const simulation = simulationRef.current;
        return () => {
            simulation.running = false;
            cancelAnimationFrame(simulation.animationId);
        };
    }, []);


    // --- Interaction Handlers ---

    // Viewport Pan/Zoom - Attached via ref to allow non-passive event
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onWheel = (e) => {
            e.preventDefault();
            const scaleAdjustment = -e.deltaY * 0.001;
            setViewport(prev => {
                const newScale = Math.min(Math.max(prev.scale + scaleAdjustment, 0.2), 3);
                return { ...prev, scale: newScale };
            });
        };

        container.addEventListener('wheel', onWheel, { passive: false });
        return () => container.removeEventListener('wheel', onWheel);
    }, []);

    const startCanvasDrag = (e) => {
        if (draggedNodeId) return; // Prioritize node drag
        setIsDraggingCanvas(true);
        setLastMousePos({ x: e.clientX, y: e.clientY });
    };

    const handleGlobalMove = (e) => {
        // Node Drag
        if (draggedNodeId) {
            const node = simulationRef.current.nodes.find(n => n.id === draggedNodeId);
            if (node) {
                // Determine scale-adjusted delta
                // But easier: map mouse directly to world space?
                // Simple approach: delta / scale
                const dx = (e.clientX - lastMousePos.x) / viewport.scale;
                const dy = (e.clientY - lastMousePos.y) / viewport.scale;

                node.x += dx;
                node.y += dy;
                // Zero velocity while dragging
                node.vx = 0;
                node.vy = 0;

                setLastMousePos({ x: e.clientX, y: e.clientY });
            }
            return;
        }

        // Canvas Drag
        if (isDraggingCanvas) {
            const dx = e.clientX - lastMousePos.x;
            const dy = e.clientY - lastMousePos.y;
            setViewport(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
            setLastMousePos({ x: e.clientX, y: e.clientY });
        }
    };

    const handleGlobalUp = () => {
        if (draggedNodeId) {
            const node = simulationRef.current.nodes.find(n => n.id === draggedNodeId);
            if (node) node.isDragging = false;
            setDraggedNodeId(null);
        }
        setIsDraggingCanvas(false);
    };

    const startNodeDrag = (nodeId, e) => {
        e.stopPropagation(); // Stop canvas drag
        const node = simulationRef.current.nodes.find(n => n.id === nodeId);
        if (node) {
            node.isDragging = true;
            node.vx = 0;
            node.vy = 0;
        }
        setDraggedNodeId(nodeId);
        setLastMousePos({ x: e.clientX, y: e.clientY });
    };

    // Node Interaction
    const handleNodeClick = (nodeId) => {
        // Optional: Expand/Collapse logic here
    };

    return (
        <div className="h-full flex flex-col relative overflow-hidden select-none bg-slate-50">
            <div className="absolute top-4 left-4 z-40 bg-white/90 backdrop-blur p-2 rounded-lg shadow-sm border border-slate-200 text-xs text-slate-600 flex flex-col gap-1 pointer-events-none">
                <div className="flex items-center gap-2"><Move size={14} /> Trascina sfondo per muoverti</div>
                <div className="flex items-center gap-2"><BrainCircuit size={14} /> Trascina nodi per riordinare</div>
            </div>

            <div className="absolute top-4 right-4 z-40 flex flex-col gap-2">
                <button onClick={() => setViewport(v => ({ ...v, scale: v.scale + 0.1 }))} className="p-2 bg-white shadow rounded-lg hover:bg-slate-50"><ZoomIn size={18} /></button>
                <button onClick={() => setViewport(v => ({ ...v, scale: v.scale - 0.1 }))} className="p-2 bg-white shadow rounded-lg hover:bg-slate-50"><ZoomOut size={18} /></button>
            </div>

            <div
                ref={containerRef}
                className={`relative flex-1 overflow-hidden w-full h-full ${isDraggingCanvas ? 'cursor-grabbing' : 'cursor-default'}`}
                onMouseDown={startCanvasDrag}
                onMouseMove={handleGlobalMove}
                onMouseUp={handleGlobalUp}
                onMouseLeave={handleGlobalUp}
                style={{ touchAction: 'none' }}
            >
                <div
                    className="absolute w-full h-full transform origin-center will-change-transform"
                    style={{
                        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
                        transformOrigin: '50% 50%'
                    }}
                >
                    {/* Center Reference */}
                    <div className="absolute top-1/2 left-1/2" style={{ transform: 'translate(-50%, -50%)' }}>

                        {/* Edges */}
                        <svg className="absolute top-0 left-0 overflow-visible pointer-events-none" style={{ left: -5000, top: -5000, width: 10000, height: 10000 }}>
                            {edges.map(edge => {
                                const source = nodes.find(n => n.id === edge.source);
                                const target = nodes.find(n => n.id === edge.target);
                                if (!source || !target) return null;

                                return (
                                    <g key={edge.id}>
                                        <line
                                            x1={source.x + 5000} y1={source.y + 5000}
                                            x2={target.x + 5000} y2={target.y + 5000}
                                            stroke="#cbd5e1" strokeWidth="2"
                                        />
                                        {edge.label && (
                                            <text
                                                x={(source.x + target.x) / 2 + 5000}
                                                y={(source.y + target.y) / 2 + 5000}
                                                textAnchor="middle"
                                                fill="#64748b"
                                                fontSize="10"
                                                dy="-5"
                                                className="bg-white/80"
                                            >
                                                {edge.label}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                        </svg>

                        {/* Nodes */}
                        {nodes.map(node => (
                            <div
                                key={node.id}
                                onMouseDown={(e) => startNodeDrag(node.id, e)}
                                onClick={() => handleNodeClick(node.id)}
                                className={`absolute transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center 
                                    cursor-grab active:cursor-grabbing hover:z-50 transition-shadow duration-200
                                `}
                                style={{ left: node.x, top: node.y }}
                            >
                                <div className={`
                                    w-14 h-14 rounded-full flex items-center justify-center shadow-lg border-4 transition-colors z-10 relative
                                    ${node.id === 'root' ? 'bg-indigo-600 border-indigo-200 text-white' : 'bg-white border-white text-slate-700'}
                                    ${draggedNodeId === node.id ? 'ring-2 ring-indigo-400' : ''}
                                `}>
                                    <BrainCircuit size={20} />
                                </div>

                                <div className="mt-2 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg shadow-sm border border-slate-100 max-w-[150px] text-center z-20 pointer-events-none select-none">
                                    <span className="text-xs font-bold text-slate-700 block truncate">
                                        {node.label}
                                    </span>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default MindMapView;
